import React from 'react';

/**
 * Minimal inline-markdown renderer shared by agent message bubbles.
 * Same proven logic as the profiling agent's bubble (bold / `code` /
 * [links] / bare URLs, headings, bullets) — extracted here so new
 * agents don't duplicate it. No external markdown library.
 */

export function renderInline(text: string, isUser: boolean, keyBase: string): React.ReactNode[] {
  const linkClass = isUser
    ? 'underline decoration-white/60 hover:decoration-white'
    : 'text-cargo-accent hover:underline';
  const codeClass = isUser
    ? 'px-1 py-0.5 rounded bg-white/20 font-mono text-[12px]'
    : 'px-1 py-0.5 rounded bg-cargo-hover text-cargo-accent font-mono text-[12px]';

  const TOKEN = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\)|https?:\/\/[^\s)]+|\/[\w-]+\/[\w/\-?&=.#%]+)/g;
  const out: React.ReactNode[] = [];
  let last = 0;
  let k = 0;
  let m: RegExpExecArray | null;
  while ((m = TOKEN.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**') && tok.endsWith('**')) {
      out.push(<strong key={`${keyBase}-b${k++}`}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('`') && tok.endsWith('`')) {
      out.push(<code key={`${keyBase}-c${k++}`} className={codeClass}>{tok.slice(1, -1)}</code>);
    } else if (tok.startsWith('[')) {
      const mm = /\[([^\]]+)\]\(([^)]+)\)/.exec(tok);
      if (mm) {
        const ext = /^https?:/i.test(mm[2]);
        out.push(
          <a key={`${keyBase}-l${k++}`} href={mm[2]}
             target={ext ? '_blank' : undefined}
             rel={ext ? 'noopener noreferrer' : undefined}
             className={linkClass}>{mm[1]}</a>,
        );
      } else {
        out.push(tok);
      }
    } else {
      const ext = /^https?:/i.test(tok);
      out.push(
        <a key={`${keyBase}-u${k++}`} href={tok}
           target={ext ? '_blank' : undefined}
           rel={ext ? 'noopener noreferrer' : undefined}
           className={linkClass}>{tok}</a>,
      );
    }
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out.length ? out : [text];
}

export function renderMarkdown(text: string, isUser: boolean): React.ReactNode {
  const lines = text.split('\n');
  const out: React.ReactNode[] = [];
  let bullets: React.ReactNode[] = [];
  let bi = 0;

  const flush = () => {
    if (bullets.length) {
      out.push(
        <ul key={`ul-${out.length}`} className="list-disc ml-5 my-1 space-y-0.5">
          {bullets}
        </ul>,
      );
      bullets = [];
    }
  };

  lines.forEach((line, i) => {
    const t = line.trim();
    const heading = /^#{1,4}\s+(.*)$/.exec(t);
    const bullet = /^[•\-*]\s+(.*)$/.exec(t);
    if (heading) {
      flush();
      out.push(
        <div key={`h-${i}`} className="font-semibold text-[14px] mt-2 mb-1 first:mt-0">
          {renderInline(heading[1], isUser, `h${i}`)}
        </div>,
      );
    } else if (bullet) {
      bullets.push(
        <li key={`li-${i}-${bi++}`}>{renderInline(bullet[1], isUser, `b${i}`)}</li>,
      );
    } else if (t === '') {
      flush();
      if (i > 0 && i < lines.length - 1) {
        out.push(<div key={`sp-${i}`} className="h-1.5" />);
      }
    } else {
      flush();
      out.push(
        <div key={`p-${i}`} className="my-0.5">
          {renderInline(line, isUser, `p${i}`)}
        </div>,
      );
    }
  });
  flush();
  return out;
}
